import { makeStyles, AppBar, IconButton } from "@material-ui/core";
import GetAppIcon from '@material-ui/icons/GetApp';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import { Button, CompactInput, Flex, getIdFromPath, Loading, Page, PageContainer, PrimaryButton, TilesList, Toolbar, useLinkedLocation, AddFilter, FiltersList, SelectSortBy, CsvDownloader, CsvMapping, asDate, csvHyperlink, asDateTime } from "lqx-react";
import { Linked, useCollection, useEvent, useIO, useLinked, useModel } from "@type-r/react";
import * as React from "react";
import { acceptInvoices, AcceptInvoicesData, GetInvoiceStatusAsText, InvoiceFilters, InvoiceModel } from "../../../../api/invoice";
import { RemittancePage } from "../inmatch/remittance";
import { CategoryTab, PromiseToPayDateSelectedInvoiceIdContext } from "./common";
import { InvoiceDetails } from "./details";
import { InvoiceHeader, InvoicesState } from "./header";
import { InvoiceGrid } from "./invoices-grid";
import { InvoiceTile } from "./invoices-list";
import { filterLengthThreshold } from "api/common";
import { globalStore } from "api/store";
import { ShowGeneralDialog } from "./dialogs/index";
import { DialogType } from "./details/dialogs/index";
import { Events } from "@type-r/models";

import { StatementOfCustomer } from './statement'

const defaultSort = {
    attr: "lastUpdatedUtc",
    asc: false
};

export const InvoicePage = () => {
    const state = useModel(InvoicesState);
    const $category = useLinkedLocation(2, "all") as Linked<CategoryTab>,
        category = $category.value;

    const invoiceId = category === 'details' ? getIdFromPath(3) : null;

    const isReady = useIO( async () => globalStore.hasPendingIO() );

    return !isReady ? <Loading /> :
        !invoiceId ? (
            <PageContainer
                header={
                    <InvoiceHeader
                        $category={$category}
                        state={state}
                    />
                }
            >
                {
                    category === 'remittance-advices' ? <RemittancePage noContainer /> :
                        category === 'statement' ? <StatementOfCustomer /> :
                            <InvoicesPage category={category} state={state} />
                }
            </PageContainer>
        ) : (
            <InvoiceDetails invoiceId={invoiceId} />
        );
};

export const InvoicesPage = ({ category, state }: {
    state: InvoicesState
    category: CategoryTab
}) => {
    const classes = useStyles();
    const invoices = useCollection.of(InvoiceModel);
    const selected = useCollection.subsetOf(invoices);

    useEvent(Events, "Invoice:close", () => selected.reset());

    const invoiceFilter = useModel(InvoiceFilters);
    const isInvoiceFilterReady = useIO(async () => globalStore.relationships.hasPendingIO());
    const
        $sort = useLinked(defaultSort),
        sort = $sort.value,
        $isGrid = useLinked(false),
        $promiseToPayDateSelectedInvoiceId = useLinked<string>(null),
        $acceptButtonClicked = useLinked(false);

    const $promiseToPayDate = useLinked<Date>(null).check(
        newDate => !$acceptButtonClicked.value || !!newDate
    );

    // Apply filter
    React.useMemo(() => {
        if (isInvoiceFilterReady) {
            invoiceFilter.initializeOptions(globalStore.relationships);
        }
    }, [isInvoiceFilterReady]);

    const relationships = React.useMemo(() => {
        return globalStore.relationships;
    }, [globalStore.relationships]);

    const isReady = useIO(
        async () => {
            if(!isInvoiceFilterReady) return;
            const result = await InvoiceModel.search(invoiceFilter.mapToInvoiceQuery(sort, category, relationships));
            invoices.set(result, { parse: true });
        },
        [isInvoiceFilterReady,
            filterLengthThreshold(invoiceFilter.filterAll),
            invoiceFilter["_changeToken"],
            category,
            sort]
    );

    const shouldShowAcceptMultipleControls =
        (category === "all" || category === "pending") &&
        (
            state.selected.filter(invoice => invoice.canBeAccepted && invoice.isParticipantApprover).length > 1 ||
            state.selected.filter(invoice => invoice.canEditPromiseToPay && invoice.isParticipantApprover).length > 1
        );

    const editPromiseToPay = state.selected.every(i => i.canEditPromiseToPay);
    const acceptPromiseToPay = state.selected.every(invoice => invoice.canBeAccepted);
    const $dialog = useLinked<DialogType>(null);

    return (
        category === 'remittance-advices' ?
            <RemittancePage pathLength={2} />
            :
            <Page
                maxWidth={false}
                top={<>
                    <AppBar position="static" color="transparent">
                        <Toolbar>
                            {
                                selected.length ? (
                                    <Button onClick={() => selected.reset()}>
                                        Clear selection
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => selected.add(invoices.models)}
                                        disabled={!invoices.length}
                                    >
                                        Select All
                                    </Button>
                                )
                            }
                            <ShowGeneralDialog
                                model={selected}
                                $dialog={$dialog}
                            />
                            <div className={classes.filterContainer}>
                                <AddFilter filter={invoiceFilter} filterAllPlaceholder="Filter by INV#, PO#, Quote#, Package Slip#" />
                            </div>
                            <Flex grow />
                            {
                                showCloseButton(category) ? (
                                    <PrimaryButton
                                        variant="contained"
                                        disabled={(selected.length == 0)}
                                        onClick={() => $dialog.set('close')}
                                    >
                                        Close Invoices
                                    </PrimaryButton>
                                ) : null
                            }
                            <SelectSortBy
                                $attr={$sort.$.attr}
                                $asc={$sort.$.asc}
                                options={(invoices.model as any).sortBy}
                            />
                            <CsvDownloader filename="invoices" mapping={csvFields} data={invoices.models}>
                                <IconButton aria-label="Download" size="small">
                                    <GetAppIcon />
                                </IconButton>
                            </CsvDownloader>
                            <IconButton size="small" onClick={() => $isGrid.set(!$isGrid.value)}>
                                <ViewColumnIcon />
                            </IconButton>
                        </Toolbar>
                        <FiltersList filter={invoiceFilter} />
                    </AppBar>
                </>
                }

                bottom={
                    <Toolbar>
                        {shouldShowAcceptMultipleControls ? (
                            <>
                                <Flex grow />

                                <Flex row className={classes.acceptMultipleInvoicesControls}>
                                    <CompactInput
                                        date
                                        label="Promise to Pay"
                                        minDate={new Date()}
                                        $value={$promiseToPayDate}
                                    />
                                    <PrimaryButton
                                        variant="contained"
                                        disabled={!acceptPromiseToPay || !editPromiseToPay}
                                        onClick={async () => {
                                            $acceptButtonClicked.set(true);

                                            if (!$promiseToPayDate.value) {
                                                return;
                                            }

                                            const data: AcceptInvoicesData = {
                                                acceptedInvoiceIds: selected.map(x => x.id),
                                                promiseToPayDate: $promiseToPayDate.value
                                            };
                                            const updatedInvoices = await acceptInvoices(data, editPromiseToPay);
                                            updatedInvoices.forEach(updated => {
                                                const toUpdate = selected.get(updated.id);
                                                toUpdate.updateInvoiceFromAcceptData(updated);
                                            });

                                            selected.reset();
                                        }}
                                    >
                                        {editPromiseToPay ? 'Edit' : 'Accept'}
                                    </PrimaryButton>
                                </Flex>
                            </>
                        ) : null}
                    </Toolbar>
                }
            >
                {
                    isReady && isInvoiceFilterReady ? (
                        $isGrid.value ? (
                            <InvoiceGrid items={invoices.models} />
                        ) : (
                            <PromiseToPayDateSelectedInvoiceIdContext.Provider
                                value={$promiseToPayDateSelectedInvoiceId}
                            >
                                <TilesList
                                    expandedAppBar={state.expandedAppBar}
                                    items={invoices.models}
                                    itemSize={60}
                                    renderer={InvoiceTile(
                                        selected.filter(inv => inv.canBeAccepted).length > 1
                                    )}
                                    selected={selected}
                                />
                            </PromiseToPayDateSelectedInvoiceIdContext.Provider>
                        )
                    ) : <Loading />
                }

            </Page>
    )

};

const csvFields: CsvMapping<InvoiceModel> = {

    "Invoice Status": x => GetInvoiceStatusAsText(x.invoiceStatus),
    "INV #": "invoiceNumber",
    "PO #": "poNumber",
    "Quote ID": "quoteId",
    "SO ID": "salesOrderId",
    "Invoiced Amount": "invoicedAmount",
    "currency": "currency",
    "Invoice Due Date": x => asDate(x.invoiceDueDateUtc),
    "Invoice Create Date": x => asDate(x.invoiceCreatedDateUtc),
    "Invoice Date": x => asDate(x.invoiceDateUtc),
    "System Create Date": x => asDateTime(x.createdOnUtc),
    "Last Updated": x => asDateTime(x.lastUpdatedUtc),
    "Payment Completion Date": x => asDate(x.paymentCompletionDateUtc),
    "Payment Date": x => asDate(x.paymentDateUtc),
    "Payment Period": "paymentPeriod",
    "Discount Period": "discountPeriod",
    "Payment Terms": "paymentTerms",
    // TODO: Dispute props - included in API
    "Dispute Number": "activeDisputeId",
    "Invoice Details URL": x => csvHyperlink([window.location.href, x.id].join('/'))
}

const showCloseButton = (category: CategoryTab) => {
    return category == 'paymentinprogress';
}

const useStyles = makeStyles({
    acceptMultipleInvoicesControls: {
        "& > *": {
            marginTop: "auto",
            marginBottom: "auto"
        }
    },
    filterContainer: {
        minWidth: "450px"
    }
});
